/* FILE: ./frontend/src/features/canada/pages/TradeSimulator.jsx */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useTranslation, Trans } from 'react-i18next'; 

// ⚡ THE SYNONYM DICTIONARY (Fast Fallback)
const TRADE_SYNONYMS = [
    { keywords: ['beam', 'h-beam', 'i-beam', 'structural steel', 'girder'], code: '7216.32', description: 'Structural Steel H/I/U Beams & Sections' },
    { keywords: ['pipe', 'tube', 'tubing', 'hollow'], code: '7304.00', description: 'Steel Pipes, Tubes and Hollow Profiles' },
    { keywords: ['lumber', 'wood', 'timber', 'board', 'plywood'], code: '4407.11', description: 'Sawn Wood, Lumber & Timber (Coniferous)' },
    { keywords: ['wire', 'cable', 'electrical'], code: '8544.11', description: 'Insulated Copper Wire & Electrical Cables' },
    { keywords: ['cement', 'concrete', 'mortar'], code: '2523.29', description: 'Portland Cement & Hydraulic Cements' },
    { keywords: ['drywall', 'gypsum', 'plasterboard'], code: '6809.11', description: 'Plasterboards & Drywall Panels' }
];

const CONSTRUCTION_CHAPTERS = ['25', '32', '39', '44', '68', '69', '70', '72', '73', '74', '76', '83'];
const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

export default function TradeSimulator() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(); 
  const dropdownRef = useRef(null);
  
  // ⚡ FEATURE FLAG: Controls CSSP display and logic
  const isCSSPEnabled = process.env.REACT_APP_ENABLE_CSSP_REBATE === 'true';

  const [formData, setFormData] = useState({
    origin: 'ON',
    origin_postal: '',
    destination: 'BC',
    dest_postal: '',
    value: 50000,
    weight: '',
    material_grade: '',
    transport_mode: 'truck' // ⚡ ADDED: defaults to standard LTL/FTL
  });

  // ⚡ AUTOCOMPLETE STATE
  const [searchTerm, setSearchTerm] = useState('');
  const [activeHsCode, setActiveHsCode] = useState(''); 
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchingDb, setIsSearchingDb] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [limitReached, setLimitReached] = useState(false);
  const [proLimitReached, setProLimitReached] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // ⚡ LIVE AI ENGINE STATE
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReport, setAuditReport] = useState(null);
  const [pendingAutoRun, setPendingAutoRun] = useState(false);
  
  // ⚡ UX: STATE MEMORY TRACKER & LOADING CAROUSEL
  const lastParamsRef = useRef(null);
  const [loadingStep, setLoadingStep] = useState(0);

  const [cameFromArbitrage, setCameFromArbitrage] = useState(false);
  const cachedSummaryRef = useRef(null);

  // --- ARBITRAGE HAND-OFF LOGIC (Populates directly without double-running Simulator) ---
  useEffect(() => {
    if (location.state?.autoRunAudit) {
      const { 
          autoFillCode, autoFillOrigin, autoFillDestination, autoFillValue, 
          autoFillOriginPostal, autoFillDestPostal, existingFlashSummary,
          existingCompliance
      } = location.state;
      
      setCameFromArbitrage(true);
      
      const nextOrigin = autoFillOrigin || formData.origin;
      const nextOriginPostal = autoFillOriginPostal || formData.origin_postal;
      const nextDest = autoFillDestination || formData.destination;
      const nextDestPostal = autoFillDestPostal || formData.dest_postal;
      const nextValue = autoFillValue || formData.value;

      setFormData(prev => ({
        ...prev,
        origin: nextOrigin,
        origin_postal: nextOriginPostal,
        destination: nextDest,
        dest_postal: nextDestPostal,
        value: nextValue
      }));
      
      if (autoFillCode) {
          setSearchTerm(`Arbitrage Target (${autoFillCode})`); 
          setActiveHsCode(autoFillCode);
      }

      if (existingFlashSummary) {
          cachedSummaryRef.current = existingFlashSummary;
      }

      // ⚡ FIX POINT 3: If ArbitrageMatrix already ran the simulation for this supplier,
      // populate the results directly from memory instead of burning another simulation run!
      if (existingCompliance) {
          const validComparisons = Array.isArray(existingCompliance.comparisons)
              ? existingCompliance.comparisons.filter(Boolean)
              : (existingCompliance.financials && existingCompliance.traffic_light !== 'red' ? [existingCompliance.financials] : []);

          lastParamsRef.current = {
              origin: nextOrigin,
              origin_postal: nextOriginPostal,
              destination: nextDest,
              dest_postal: nextDestPostal,
              value: nextValue,
              weight: formData.weight,
              material_grade: formData.material_grade,
              hs_code: autoFillCode
          };

          setResults({
              status: existingCompliance.traffic_light,
              warnings: [existingCompliance.compliance_summary],
              actions: existingCompliance.required_actions || [],
              citation: existingCompliance.citation || null,
              weight: formData.weight,
              material_grade: formData.material_grade,
              flashSummary: existingCompliance.flashSummary || existingFlashSummary || null,
              financialsHistory: validComparisons
          });
          setPendingAutoRun(false);
      } else {
          setPendingAutoRun(true);
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]); // eslint-disable-line

  useEffect(() => {
    if (pendingAutoRun && activeHsCode && formData.origin) {
      setPendingAutoRun(false);
      calculateScenario(); 
    }
  }, [pendingAutoRun, activeHsCode, formData.origin]); // eslint-disable-line

  // --- UX: LOADING CAROUSEL EFFECT ---
  useEffect(() => {
      let interval;
      if (auditLoading) {
          setLoadingStep(0);
          interval = setInterval(() => {
              setLoadingStep((prev) => (prev < 5 ? prev + 1 : prev));
          }, 8000); 
      }
      return () => clearInterval(interval);
  }, [auditLoading]);

  // --- AUTOCOMPLETE LOGIC ---
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
    }

    const fetchSuggestions = async () => {
        setIsSearchingDb(true);
        try {
            const queryLower = searchTerm.toLowerCase();
            let allMatches = [];

            // 1. Check Hardcoded Synonyms (Fast fallback)
            TRADE_SYNONYMS.forEach(item => {
                if (item.keywords.some(kw => queryLower.includes(kw))) {
                    allMatches.push({ code: item.code, description: item.description });
                }
            });

            // 2. Query live 'hs_synonyms' Supabase table
            const { data: dbSynonyms } = await supabase
                .from('hs_synonyms')
                .select('colloquial_term, target_hs_code')
                .ilike('colloquial_term', `%${queryLower}%`)
                .limit(5);

            if (dbSynonyms) {
                dbSynonyms.forEach(s => {
                    allMatches.push({ code: s.target_hs_code, description: s.colloquial_term });
                });
            }

            // 3. Query official HS Database
            let query = supabase.from('hs_codes').select('code, description');
            const numericSearch = searchTerm.replace(/[^0-9]/g, '');
            
            if (numericSearch.length >= 2) {
                query = query.ilike('code', `${numericSearch}%`);
            } else {
                const words = searchTerm.trim().split(/\s+/).filter(w => w.length > 2);
                const chapterFilter = CONSTRUCTION_CHAPTERS.map(ch => `code.ilike.${ch}%`).join(',');
                
                query = query.or(chapterFilter);
                words.forEach(w => {
                   query = query.ilike('description', `%${w}%`);
                });
            }

            const { data: officialCodes, error } = await query.limit(5);
            if (error) throw error;
            
            // 4. Merge all sources and remove duplicates
            const combined = [...allMatches, ...(officialCodes || [])];
            const unique = Array.from(new Map(combined.map(item => [item.code, item])).values());
            
            setSuggestions(unique.slice(0, 6));
            setShowDropdown(unique.length > 0);

        } catch (err) {
            console.error("Autocomplete Error:", err);
        } finally {
            setIsSearchingDb(false);
        }
    };

    if (searchTerm.includes('(') && searchTerm.includes(')')) return;

    const delayDebounceFn = setTimeout(() => { fetchSuggestions(); }, 300); 
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const handleSuggestionClick = (item) => {
      setSearchTerm(`${item.description} (${item.code})`);
      setActiveHsCode(item.code);
      setShowDropdown(false);
  };

  const logAudit = (actionType, status, errorMsg = null, additionalParams = {}) => {
      if (!user?.id) return;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      fetch(`${apiUrl}/audit/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
          body: JSON.stringify({
              action_type: actionType,
              status: status,
              error_message: errorMsg,
              parameters: additionalParams
          })
      }).catch(() => {});
  };

  // ⚡ UPDATED: calculation function safely handles empty comparisons on blocked items
  const calculateScenario = async (isCompareAll = false) => {
    setLoading(true);
    setError(null);
    setLimitReached(false);
    setProLimitReached(false);
    setAuditReport(null); 

    const payloadHsCode = activeHsCode || searchTerm.replace(/[^0-9.]/g, '').slice(0, 12);
    if (!payloadHsCode) {
        setError("Please select a valid material or provide an HS Code.");
        setLoading(false);
        return;
    }

    try {
      const currentParams = {
          origin: formData.origin,
          origin_postal: formData.origin_postal,
          destination: formData.destination,
          dest_postal: formData.dest_postal,
          value: formData.value,
          weight: formData.weight,
          material_grade: formData.material_grade,
          hs_code: payloadHsCode
      };

      const isOnlyModeChanged = lastParamsRef.current && JSON.stringify(lastParamsRef.current) === JSON.stringify(currentParams);
      lastParamsRef.current = currentParams;

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      
      const targetMode = isCompareAll ? 'all' : formData.transport_mode;
      const payload = {
          ...formData,
          hs_code: payloadHsCode,
          transport_mode: targetMode,
          cssp_enabled: isCSSPEnabled
      };
      
      const response = await fetch(`${apiUrl}/compliance/simulate`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'x-user-id': user?.id,
            'Accept-Language': i18n.language 
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (!response.ok) {
        if (data.code === 'LIMIT_REACHED') {
            setLimitReached(true);
            return;
        }
        if (data.code === 'PRO_LIMIT_REACHED') {
            setProLimitReached(true);
            return;
        }
        throw new Error(data.error || 'Simulation failed');
      }

      setResults(prevResults => {
          const validComparisons = Array.isArray(data.comparisons) ? data.comparisons.filter(Boolean) : [];
          let newHistory = [];
          
          if (validComparisons.length > 1) {
              newHistory = validComparisons;
          } else if (validComparisons.length === 1) {
              const singleMatrix = validComparisons[0]; 
              
              if (isOnlyModeChanged && prevResults?.financialsHistory) {
                  const existingHistory = prevResults.financialsHistory.filter(Boolean);
                  const existingIndex = existingHistory.findIndex(h => h?.mode === singleMatrix?.mode);
                  if (existingIndex >= 0) {
                      newHistory = [...existingHistory];
                      newHistory[existingIndex] = singleMatrix; 
                  } else {
                      newHistory = [...existingHistory, singleMatrix];
                  }
              } else {
                  newHistory = [singleMatrix];
              }
          }

          return {
            status: data.traffic_light || 'green', 
            warnings: [data.compliance_summary || 'Compliance check completed.'],
            actions: data.required_actions || [], 
            citation: data.citation || null,
            weight: formData.weight,
            material_grade: formData.material_grade,
            flashSummary: data.flashSummary || cachedSummaryRef.current || null,
            financialsHistory: newHistory 
          };
      });

      logAudit('TRADE_SIMULATOR', 'SUCCESS', null, { hs_code: payloadHsCode, origin: formData.origin, destination: formData.destination, value: formData.value });

    } catch (err) {
      console.error("Simulation Error:", err);
      setError("Failed to connect to the Compliance Engine.");
      logAudit('TRADE_SIMULATOR', 'ERROR', err.message, { hs_code: payloadHsCode, origin: formData.origin, destination: formData.destination });
    } finally {
      setLoading(false);
    }
  };

  const runDeepAudit = async () => {
    const hasAuditAccess = userProfile?.role === 'admin' || userProfile?.buyer_tier === 'pro' || userProfile?.buyer_tier === 'pro_volume' || (userProfile?.ai_audit_credits || 0) > 0;
    if (!hasAuditAccess) {
        alert("Deep Compliance Audits require a Pro Buyer account.");
        return;
    }

    if (results?.status === 'red') {
        alert("Deep Compliance Audits cannot be run on prohibited or out-of-scope materials.");
        return;
    }

    setAuditLoading(true);
    setAuditReport(null);

    const payloadHsCode = activeHsCode || searchTerm.replace(/[^0-9.]/g, '').slice(0, 12);

    try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
        
        const response = await fetch(`${apiUrl}/compliance/deep-audit`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'x-user-id': user?.id,
                'Accept-Language': i18n.language 
            },
            body: JSON.stringify({
                hsCode: payloadHsCode,
                originProvince: formData.origin,
                destProvince: formData.destination,
                productDesc: formData.material_grade || searchTerm || 'Industrial Materials'
            })
        });
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || data.error || 'AI Audit failed');
        }

        setAuditReport(data.report);
        logAudit('DEEP_AUDIT', 'SUCCESS', null, { hs_code: payloadHsCode, destination: formData.destination });

    } catch (err) {
        console.error("AI Audit Error:", err);
        alert(err.message);
        logAudit('DEEP_AUDIT', 'ERROR', err.message, { hs_code: payloadHsCode, destination: formData.destination });
    } finally {
        setAuditLoading(false);
    }
  };

  const handlePdfClick = () => {
      if (userProfile?.buyer_tier !== 'pro' && userProfile?.buyer_tier !== 'pro_volume' && userProfile?.role !== 'admin') {
          alert("Downloadable Compliance Reports are a Pro Buyer feature.");
          return;
      }
      setShowPdfModal(true);
  };

  const executeDownload = () => {
      setShowPdfModal(false);
      window.print();
  };

  const getLoadingMessage = () => {
      const messages = [
          t('simulator.audit_step_0', "Initializing AI Compliance Engine..."),
          t('simulator.audit_step_1', "Scanning CFTA provincial exceptions..."),
          t('simulator.audit_step_2', "Cross-referencing destination tax codes..."),
          t('simulator.audit_step_3', "Analyzing safety and certification requirements..."),
          t('simulator.audit_step_4', "Compiling final regulatory report..."),
          t('simulator.audit_step_5', "Formatting Deep Audit results...")
      ];
      return messages[loadingStep] || messages[messages.length - 1];
  };

  const renderArbitrageInsight = () => {
      if (formData.origin === formData.destination || results?.status === 'red') return null;
      return (
          <div className="mt-6 bg-indigo-50/80 border border-indigo-100 rounded-2xl p-6 shadow-sm print:hidden animate-fade-in">
              <h4 className="text-xs font-bold text-indigo-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="text-lg">💡</span> {t('simulator.insight_title', 'Strategic Sourcing Insight')}
              </h4>
              <p className="text-sm text-indigo-900 leading-relaxed mb-4">
                  <Trans i18nKey="simulator.insight_desc" values={{ origin: formData.origin, destination: formData.destination }}>
                    You are calculating an inter-provincial import from <strong>{formData.origin}</strong>. Sourcing this material locally within <strong>{formData.destination}</strong> instead eliminates all CFTA compliance friction and minimizes your freight overhead.
                  </Trans>
              </p>
              <button 
                onClick={() => navigate(`/directory?province=${formData.destination}&search=${activeHsCode || searchTerm}`)}
                className="text-xs font-bold text-indigo-700 hover:text-white border border-indigo-200 bg-white hover:bg-indigo-600 px-5 py-2.5 rounded-xl shadow-sm transition-all w-full md:w-auto"
              >
                  {t('simulator.find_local_btn', { province: formData.destination })}
              </button>
          </div>
      );
  };

  if (!user) {
      return (
          <div className="min-h-screen bg-gray-50 py-12 px-4 flex items-center justify-center font-sans">
              <div className="bg-white p-10 rounded-3xl shadow-xl max-w-md w-full text-center border border-gray-200">
                  <div className="text-6xl mb-6">🔐</div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('simulator.login_title')}</h2>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                      {t('simulator.login_desc')}
                  </p>
                  <Link to="/login" className="inline-block w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-md transition-all">
                      {t('simulator.login_btn')}
                  </Link>
                  <button onClick={() => navigate(-1)} className="mt-6 text-sm text-gray-500 hover:text-gray-800 font-medium">
                      {t('simulator.go_back')}
                  </button>
              </div>
          </div>
      );
  }

  // ⚡ Define Authorization Constants for UI Rendering
  const isProOrAdmin = userProfile?.buyer_tier === 'pro' || userProfile?.buyer_tier === 'pro_volume' || userProfile?.role === 'admin';
  const isFormReady = formData.value && (activeHsCode || searchTerm.length >= 2);

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-sans text-gray-800 flex flex-col print:bg-white print:p-0 print:border-none print:overflow-visible">
      
      {/* ⚡ LAYOUT FIX 1: Using flex-col md:flex-row to prevent grid collapsing */}
      <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row gap-6 print:block print:max-w-none print:overflow-visible flex-grow">
        
        {/* SIDEBAR: Configuration Panel (Locked width to prevent squishing) */}
        <div className="w-full md:w-[360px] shrink-0 bg-white p-6 rounded-3xl shadow-sm border border-gray-200 flex flex-col print:hidden">
            
            {cameFromArbitrage && (
                <div className="mb-4 print:hidden">
                    <button 
                        onClick={() => navigate(-1)} 
                        className="flex items-center text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                        {t('directory.back_to_directory')}
                    </button>
                </div>
            )}

            <h2 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                <span>⚙️</span> {t('simulator.config_title', 'Configure Scenario')}
            </h2>
            
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.origin', 'Origin')}</label>
                        <select 
                            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-3 bg-gray-50 text-sm"
                            value={formData.origin}
                            onChange={(e) => setFormData({...formData, origin: e.target.value})}
                        >
                            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.destination', 'Destination')}</label>
                        <select 
                            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-3 bg-gray-50 text-sm"
                            value={formData.destination}
                            onChange={(e) => setFormData({...formData, destination: e.target.value})}
                        >
                            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.origin_postal', 'Origin Postal')}</label>
                        <input 
                            type="text"
                            maxLength="7"
                            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 uppercase bg-white text-sm"
                            placeholder="M5V 2H1"
                            value={formData.origin_postal}
                            onChange={(e) => setFormData({...formData, origin_postal: e.target.value.toUpperCase()})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.dest_postal', 'Dest. Postal')}</label>
                        <input 
                            type="text"
                            maxLength="7"
                            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 uppercase bg-white text-sm"
                            placeholder="V6B 1A1"
                            value={formData.dest_postal}
                            onChange={(e) => setFormData({...formData, dest_postal: e.target.value.toUpperCase()})}
                        />
                    </div>
                </div>

                <div className="relative" ref={dropdownRef}>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.material_label', 'Material / Keyword')}</label>
                    <div className={`relative transition-all duration-200 ${isFocused ? 'transform scale-[1.02]' : ''}`}>
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-400">🔍</span>
                        </div>
                        <input 
                            type="text"
                            className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-3 pl-10 pr-10 bg-white text-sm"
                            placeholder="e.g., Structural Steel"
                            autoComplete="off"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setActiveHsCode('');
                            }}
                            onFocus={() => { setIsFocused(true); if (suggestions.length > 0) setShowDropdown(true); }}
                            onBlur={() => setIsFocused(false)}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => { setSearchTerm(''); setActiveHsCode(''); setShowDropdown(false); setSuggestions([]); }}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {showDropdown && (
                        <div className="absolute z-50 w-full mt-2 bg-white border border-blue-200 rounded-xl shadow-2xl overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                            {isSearchingDb ? (
                                <div className="p-4 text-sm text-gray-500 flex items-center gap-3">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    Searching Database...
                                </div>
                            ) : (
                                <ul>
                                    {suggestions.map((item, index) => (
                                        <li 
                                            key={index}
                                            onClick={() => handleSuggestionClick(item)}
                                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 flex items-start gap-3 transition-colors"
                                        >
                                            <span className="text-blue-600 mt-0.5">🔍</span>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 line-clamp-1">{item.description}</p>
                                                <p className="text-xs font-mono text-blue-700 mt-0.5">HS Code: {item.code}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1.5">{t('simulator.material_helper', 'Auto-maps to official HS Codes.')}</p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.weight_label', 'Est. Weight / Volume')}</label>
                    <input 
                        type="text"
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 bg-white text-sm"
                        placeholder="e.g., 45,000 lbs"
                        value={formData.weight}
                        onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    />
                    <p className="text-[10px] text-gray-400 mt-1.5">{t('simulator.weight_helper', 'Required for accurate LTL logic.')}</p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.grade_label', 'Material Grade / Certs')}</label>
                    <input 
                        type="text"
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 bg-white text-sm"
                        placeholder="e.g., ASTM A992"
                        value={formData.material_grade}
                        onChange={(e) => setFormData({...formData, material_grade: e.target.value})}
                    />
                </div>
                
                {/* ⚡ TRANSPORT MODE SELECTOR WITH FLAG CONTROL */}
                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.transport_mode', 'Transport Mode')}</label>
                    <select 
                        className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-3 bg-gray-50 text-sm"
                        value={formData.transport_mode}
                        onChange={(e) => setFormData({...formData, transport_mode: e.target.value})}
                    >
                        <option value="truck">{t('simulator.mode_truck', 'Standard Truck (LTL/FTL)')}</option>
                        {isCSSPEnabled && (
                            <>
                                <option value="rail">{t('simulator.mode_rail', 'Rail (Carload) - CSSP Eligible')}</option>
                                <option value="marine">{t('simulator.mode_marine', 'Marine (Breakbulk) - CSSP Eligible')}</option>
                            </>
                        )}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">{t('simulator.value_label', 'Value (CAD)')}</label>
                    <div className="relative rounded-xl shadow-sm bg-white">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 font-bold">$</span>
                      </div>
                      <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="block w-full pl-8 pr-12 border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 py-3 font-mono text-base"
                          placeholder="0.00"
                          value={formData.value}
                          onChange={(e) => setFormData({...formData, value: e.target.value})}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">CAD</span>
                      </div>
                    </div>
                </div>

                {/* ⚡ BUTTON 1: STANDARD SINGLE-RUN */}
                <button
                    onClick={() => calculateScenario(false)}
                    disabled={loading || !isFormReady}
                    className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-black tracking-wide text-white bg-blue-600 hover:bg-blue-700 hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 mt-6"
                >
                    {loading ? (
                        <span className="flex items-center gap-3">
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          {t('simulator.analyzing', 'Analyzing...')}
                        </span>
                    ) : t('simulator.run_btn', 'Run Simulation')}
                </button>

                {/* ⚡ BUTTON 2: THE "COMPARE ALL" UPSELL BILLBOARD */}
                {isCSSPEnabled && (
                    <div className="mt-3 relative group">
                        <button
                            onClick={() => calculateScenario(true)}
                            disabled={loading || !isFormReady || !isProOrAdmin}
                            className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-sm border text-sm font-bold tracking-wide transition-all focus:outline-none
                                ${!isProOrAdmin
                                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:shadow-md'
                                }
                            `}
                        >
                            <span className="text-lg leading-none mr-2">⚡</span> {t('simulator.compare_all_btn', 'Compare All Modes (Pro)')}
                            {!isProOrAdmin && <span className="ml-2 text-gray-400">🔒</span>}
                        </button>
                        
                        {!isProOrAdmin && (
                            <p className="text-center text-[10px] text-gray-400 mt-2 font-medium">
                                <Link to="/billing" className="text-indigo-600 hover:text-indigo-800 hover:underline">Upgrade to Premium</Link> to run concurrent intermodal matrices.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>

        {/* ⚡ LAYOUT FIX 2: flex-1, min-w-0, and w-full lock the content width */}
        <div className="flex-1 min-w-0 w-full flex flex-col print:p-0">
            
            <div className="text-center md:text-left mb-8 print:hidden">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">{t('simulator.title', 'Trade Simulator (Pro-Tools)')}</h1>
                <p className="mt-2 text-base text-gray-600">{t('simulator.subtitle', 'Run "What-If" procurement scenarios to estimate landed costs and compliance risks.')}</p>
            </div>

            {proLimitReached && (
                <div className="text-center bg-gray-900 p-8 rounded-2xl shadow-lg border border-gray-800 print:hidden animate-fade-in-up">
                  <div className="text-4xl mb-4">🛡️</div>
                  <h3 className="text-xl font-bold text-white mb-2">Monthly Capacity Reached</h3>
                  <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                      You have reached the maximum allowance of <strong>{userProfile?.buyer_volume_tier === 'high_volume' ? 300 : 100} simulations</strong> for your Pro tier this month.
                  </p>
                </div>
            )}

            {limitReached && (
              <div className="text-center bg-rose-50 p-8 rounded-2xl border border-rose-200 print:hidden">
                  <h3 className="text-xl font-bold text-rose-900 mb-2">Simulation Limit Reached</h3>
                  <p className="text-rose-700 text-sm mb-6">You have used your 3 free simulations for this month.</p>
                  <Link to="/billing" className="inline-block bg-rose-600 text-white px-6 py-3 rounded-xl font-bold shadow-md">
                      Upgrade Account
                 </Link>
              </div>
            )}

            {error && !limitReached && !proLimitReached && (
              <div className="rounded-xl bg-rose-50 p-6 border border-rose-200 text-center text-rose-700 mb-6 print:hidden">
                <p className="font-bold text-lg mb-1">Engine Error</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {!results && !error && !limitReached && !proLimitReached ? (
              <div className="text-center text-gray-400 py-24 print:hidden flex flex-col items-center justify-center h-full bg-white rounded-3xl border border-gray-200 shadow-sm">
                <div className="mx-auto w-20 h-20 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
                  <span className="text-4xl">📊</span>
                </div>
                <h3 className="text-xl font-bold text-slate-800">{t('simulator.ready_title', 'Engine Ready')}</h3>
                <p className="mt-2 text-sm text-slate-500 max-w-sm">{t('simulator.ready_desc', 'Configure your shipment parameters on the left to generate a detailed compliance report.')}</p>
              </div>
            ) : results && !limitReached && !proLimitReached ? (
              <div className="space-y-6 animate-fade-in-up print:space-y-8 print:w-full print:block print:overflow-visible">
                
                {/* ⚡ PRINT SPECIFIC: The multi-column grid layout for the PDF metadata */}
                <div className="hidden print:block border-b border-gray-200 pb-8 mb-4 break-inside-avoid">
                    <div className="flex justify-between items-end border-b-2 border-slate-900 pb-6 mb-8">
                        <div>
                            <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">TariffBeater Inc.</h1>
                            <h2 className="text-xl font-semibold text-slate-600 mt-1">Deep Compliance Audit Report</h2>
                        </div>
                        <div className="text-right">
                            <div className="text-sm font-bold text-slate-500 uppercase tracking-widest">Generated On</div>
                            <div className="text-lg font-mono text-slate-900">{new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Shipment Logistics</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-xs text-gray-500">Origin Route:</span>
                                    <span className="text-xs font-bold text-gray-900">{formData.origin} {formData.origin_postal ? `(${formData.origin_postal})` : ''}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-xs text-gray-500">Destination Route:</span>
                                    <span className="text-xs font-bold text-gray-900">{formData.destination} {formData.dest_postal ? `(${formData.dest_postal})` : ''}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-xs text-gray-500">Est. Weight:</span>
                                    <span className="text-xs font-bold text-gray-900">{results.weight || 'TBD'}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Material Manifest</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-xs text-gray-500">Material Grade:</span>
                                    <span className="text-xs font-bold text-gray-900">{results.material_grade || searchTerm || 'Industrial Materials'}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-xs text-gray-500">HS Chapter Code:</span>
                                    <span className="text-xs font-mono font-bold text-gray-900">{activeHsCode || 'Verified'}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-xs text-gray-500">Base Value (CAD):</span>
                                    <span className="text-xs font-bold text-gray-900">${parseFloat(formData.value || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ⚡ PDF FIX: print:break-inside-avoid prevents status card from splitting */}
                <div className={`p-6 md:p-8 rounded-3xl shadow-sm border print:break-inside-avoid print:p-0 print:border-none print:shadow-none print:overflow-visible ${
                  results.status === 'red' ? 'bg-rose-50 border-rose-200' :
                  results.status === 'yellow' ? 'bg-amber-50 border-amber-200' :
                  'bg-[#f0fdf4] border-green-200'
                }`}>
                  <div className="flex items-start md:items-center flex-col md:flex-row gap-5 md:gap-6">
                    <div className="flex-shrink-0 print:hidden">
                      <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-inner text-3xl ${
                          results.status === 'red' ? 'bg-rose-400 text-white' :
                          results.status === 'yellow' ? 'bg-amber-400 text-white' :
                          'bg-green-400 text-white'
                      }`}>
                        {results.status === 'red' ? '⛔' : results.status === 'yellow' ? '⚠️' : '✅'}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className={`inline-block px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-3 border print:px-0 print:py-0 print:border-none print:bg-transparent ${
                        results.status === 'red' ? 'bg-rose-100 text-rose-800 border-rose-200 print:text-rose-900' :
                        results.status === 'yellow' ? 'bg-amber-100 text-amber-800 border-amber-200 print:text-amber-900' :
                        'bg-green-200 text-green-800 border-green-300 print:text-green-900'
                      }`}>
                        {results.status === 'red' ? t('simulator.status_red', 'Friction Detected') : 
                         results.status === 'yellow' ? t('simulator.status_yellow', 'Caution Advised') : t('simulator.status_green', 'Clear For Trade')}
                      </div>
                      
                      <div className="mt-1 text-sm text-gray-800">
                        <h2 className="text-xl md:text-2xl font-medium leading-snug text-gray-900 print:text-black mb-4">
                            {results.warnings?.[0]}
                        </h2>
                        
                        {results.flashSummary && (
                            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex gap-4 md:ml-6 relative overflow-hidden print:ml-0 print:border-l-4 print:border-orange-400 print:rounded-none print:shadow-none">
                                <div className="absolute top-0 left-0 w-1 h-full bg-orange-400 print:hidden"></div>
                                <div className="text-orange-400 text-xl shrink-0 mt-0.5 print:hidden">⚡</div>
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t('simulator.triage_badge', 'Gemini Flash Triage')}</span>
                                        <span className="text-[10px] italic text-gray-400 font-normal">{t('simulator.triage_disclaimer', 'AI-generated estimate.')}</span>
                                    </div>
                                    <p className="text-sm text-gray-700 leading-relaxed">{results.flashSummary}</p>
                                </div>
                            </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ⚡ THE UI MATRIX: Safely renders matrices and skips empty arrays on blocked materials */}
                <div className="space-y-6">
                    {Array.isArray(results.financialsHistory) && results.financialsHistory.filter(Boolean).map((fin, idx) => {
                        const totalLanded = Number(fin.total_landed) || 1;
                        return (
                        <div key={idx} className="bg-white rounded-3xl border border-gray-200 p-6 md:p-8 shadow-sm print:break-inside-avoid print:border-none print:p-0 print:shadow-none animate-fade-in-up">
                            <h4 className="text-xs font-black text-indigo-800 uppercase tracking-widest mb-6 print:text-black">
                                Landed Cost Analysis — Routing: {fin.mode ? String(fin.mode).toUpperCase() : 'TRUCK'}
                            </h4>
                            
                            {/* ⚡ RESTORED: The Stacked Proportional Bar Chart */}
                            <div className="mb-8 print:hidden">
                                <div className="w-full h-3 flex rounded-full overflow-hidden mb-3 bg-gray-100">
                                    <div className="bg-slate-800 h-full" style={{ width: `${((fin.product_value || 0) / totalLanded) * 100}%` }}></div>
                                    <div className="bg-rose-500 h-full" style={{ width: `${((fin.estimated_tax || 0) / totalLanded) * 100}%` }}></div>
                                    <div className="bg-amber-500 h-full" style={{ width: `${((fin.estimated_freight || 0) / totalLanded) * 100}%` }}></div>
                                </div>
                                <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-800"></span> Base Material</div>
                                    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500"></span> Est. Taxes</div>
                                    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Freight</div>
                                </div>
                            </div>

                            <div className="w-full text-sm text-left">
                                <div className="divide-y divide-gray-100">
                                <div className="flex justify-between py-4">
                                    <span className="text-gray-600 font-medium">{t('simulator.base_value', 'Base Value')}</span>
                                    <span className="font-semibold text-gray-900 font-mono">${parseFloat(fin.product_value || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                
                                <div className="flex justify-between py-4">
                                    <div>
                                        <span className="text-gray-600 font-medium">{t('simulator.est_taxes', 'Est. Taxes')}</span>
                                        <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider font-mono">{fin.tax_details}</p>
                                    </div>
                                    <span className="font-bold text-rose-600 font-mono print:text-black">+${Number(fin.estimated_tax || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                
                                {/* ⚡ GROSS ACCOUNTING: Shows Unsubsidized Freight so math adds up */}
                                <div className="flex justify-between py-4 bg-blue-50/50 px-4 -mx-4 rounded-xl print:bg-transparent print:px-0 print:mx-0">
                                    <div>
                                        <span className="text-gray-800 font-semibold">{t('simulator.est_freight', 'Est. Freight Transport')}</span>
                                        {fin.estimated_freight > 0 ? (
                                            <p className="text-xs text-blue-600 font-medium mt-1 uppercase tracking-wider print:text-gray-600">
                                                {fin.freight_details}
                                            </p>
                                        ) : (
                                            <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{t('simulator.freight_tbd', 'To Be Determined')}</p>
                                        )}
                                    </div>
                                    <span className="font-bold text-gray-900 font-mono">
                                        {fin.estimated_freight > 0 ? `+$${Number(fin.cssp_rebate > 0 ? fin.gross_freight : fin.estimated_freight).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                    </span>
                                </div>

                                {/* ⚡ CONDITIONALLY SHOW CSSP REBATE ROW ⚡ */}
                                {isCSSPEnabled && fin.cssp_rebate > 0 && (
                                    <div className="flex justify-between py-4 bg-green-50/50 px-4 -mx-4 rounded-xl border border-green-100 print:bg-transparent print:px-0 print:mx-0">
                                        <div>
                                            <span className="text-green-800 font-bold">{t('simulator.cssp_rebate_title', 'Federal CSSP Freight Rebate')}</span>
                                            <p className="text-xs text-green-600 mt-1 uppercase tracking-wider print:text-gray-600">
                                                {t('simulator.cssp_rebate_desc', 'Transport Canada Subsidy Applied')}
                                            </p>
                                        </div>
                                        <span className="font-bold text-green-600 font-mono print:text-black">
                                            -${Number(fin.cssp_rebate || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                )}

                                <div className="flex justify-between items-end pt-6 mt-2 border-t-2 border-gray-100 print:border-gray-900 print:border-t-4">
                                    <span className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">{t('simulator.total_landed', 'Total Landed Cost')}</span>
                                    <span className="text-3xl md:text-4xl font-black text-slate-900 font-mono tracking-tight print:text-black">
                                        ${Number(fin.total_landed || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>

                {results.actions && results.actions.length > 0 && (
                   <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm print:break-inside-avoid print:border-gray-400 print:shadow-none print:mt-8">
                     <div className="bg-slate-50 px-6 py-4 border-b border-gray-200 print:bg-transparent print:border-b-2 print:border-gray-900 print:px-0">
                        <h4 className="text-[10px] font-black text-gray-500 uppercase tracking-widest print:text-gray-900">{t('simulator.checklist_title', 'Compliance Action Checklist')}</h4>
                     </div>
                     <div className="p-6 space-y-4 bg-white print:px-0">
                        {results.actions.map((action, idx) => (
                           <label key={idx} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm print:shadow-none print:border-gray-200 print:p-3">
                              <input type="checkbox" className="mt-1 form-checkbox h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                              <span className="text-sm font-medium text-gray-800 leading-relaxed">{action}</span>
                           </label>
                        ))}
                     </div>
                    
                     <div className="p-6 bg-slate-900 text-white print:bg-gray-50 print:text-black print:border-t print:border-gray-300">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 print:text-gray-500 mb-2 flex items-center gap-2">
                            <span>🛡️</span> {t('simulator.broker_step', 'Broker Verification Step')}
                        </p>
                        <p className="text-sm leading-relaxed opacity-90 print:opacity-100">
                          {t('simulator.broker_desc', 'Final compliance checks must be validated by a licensed customs broker or trade professional prior to execution.')}
                        </p>
                     </div>
                   </div>
                )}

                {renderArbitrageInsight()}

                {results.status !== 'red' && (
                  <div className="space-y-4 print:hidden pt-4 border-t border-gray-100">
                      <div className="flex flex-col md:flex-row gap-4">
                          <button 
                              onClick={handlePdfClick}
                              className="flex-1 flex justify-center items-center px-4 py-4 border border-gray-300 shadow-sm text-sm font-bold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          >
                              <svg className="mr-2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {t('simulator.export_pdf', 'Export Estimate (PDF)')}
                          </button>
                          
                          <button 
                              onClick={runDeepAudit} 
                              disabled={auditLoading}
                              className="flex-1 flex justify-center items-center px-4 py-4 border border-transparent bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-bold rounded-xl transition-all disabled:opacity-75 disabled:cursor-wait"
                          >
                              {auditLoading ? (
                                  <div className="flex flex-col items-center justify-center gap-1">
                                     <div className="flex items-center gap-2">
                                         <div className="w-4 h-4 border-2 border-indigo-700 border-t-transparent rounded-full animate-spin"></div>
                                         <span>{getLoadingMessage()}</span>
                                     </div>
                                  </div>
                              ) : (
                                  <div className="flex items-center gap-2">
                                     <span className="text-lg">✨</span> {t('simulator.deep_audit_btn', 'Run Deep Compliance Audit')}
                                  </div>
                              )}
                          </button>
                      </div>
                  </div>
                )}

                {auditReport && (
                    /* ⚡ PDF FIX: Container breaks nicely, overflow is visible for large content */
                    <div className="mt-8 bg-white border border-indigo-100 rounded-3xl p-8 shadow-sm print:border-none print:shadow-none print:p-0 print:mt-12 print:overflow-visible">
                        <div className="flex items-center gap-3 mb-8 border-b border-gray-200 pb-4 print:border-gray-900 print:break-inside-avoid">
                            <h3 className="font-black text-gray-400 uppercase tracking-widest print:text-black">{t('simulator.audit_report_title', 'Gemini AI Compliance Report')}</h3>
                        </div>
                        
                        <div className="prose prose-sm md:prose-base max-w-none text-gray-700 leading-relaxed marker:text-indigo-500 prose-headings:text-slate-900 prose-headings:font-bold prose-strong:text-slate-900 print:text-black">
                             <div dangerouslySetInnerHTML={{ __html: auditReport }} />
                        </div>
                    </div>
                )}

              </div>
            ) : null}
            
            {/* ⚡ LEGAL DISCLAIMER FIX: Wrapped with print:break-inside-avoid to stick to bottom cleanly */}
            <div className="mt-auto pt-8 border-t border-gray-200 text-[10px] text-gray-400 text-justify leading-relaxed w-full print:mt-16 print:pt-4 print:border-t-2 print:border-gray-900 print:break-inside-avoid print:text-gray-500">
               <Trans i18nKey="simulator.footer_notice">
                 <strong>LEGAL & REGULATORY NOTICE:</strong> This simulator provides AI-generated estimates for landed costs, freight routing, and inter-provincial CFTA friction. Tariffbeater Inc. is a software platform, not a licensed customs broker, tax professional, or legal advisor. Freight rates apply current industry baseline averages, Lane Capacity Premiums, and National Fuel Surcharges (FCA). Tax rates and CFTA exemptions are dynamic and subject to final assessment by the CRA and relevant provincial authorities. You must independently verify all financial and regulatory obligations with a licensed professional before executing a Purchase Order. Tariffbeater assumes no liability for procurement discrepancies or trade delays.
               </Trans>
            </div>

        </div>
      </div>

      {showPdfModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4 print:hidden backdrop-blur-sm">
            <div className="bg-white rounded-3xl p-8 md:p-10 max-w-lg w-full shadow-2xl animate-fade-in-up border border-slate-200">
                <div className="flex items-center gap-4 mb-6">
                   <div className="bg-indigo-100 p-3 rounded-full text-indigo-700 text-2xl">⚖️</div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">{t('simulator.modal_title')}</h3>
                </div>
                
                <p className="text-slate-600 text-base mb-8 leading-relaxed">
                    <Trans i18nKey="simulator.modal_desc">
                        By downloading this scenario, you acknowledge that this is an <strong>estimate</strong> and does not constitute official legal or tax advice. You must verify final requirements with your internal compliance officer or broker prior to purchase.
                    </Trans>
                </p>
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                    <button onClick={() => setShowPdfModal(false)} className="px-5 py-3 text-slate-600 hover:bg-slate-50 rounded-xl font-bold transition-colors">
                         {t('simulator.modal_cancel')}
                    </button>
                    <button onClick={executeDownload} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-md transition-all">
                        {t('simulator.modal_confirm')}
                    </button>
                </div>
            </div>
        </div>
      )}

    </div>
  );
}